import FormErrorHelperText from '@/components/Form/FormErrorHelperText';
import UploadIcon from '@/components/Icons/UploadIcon';
import { GREY } from '@/constants/color';
import { DeleteOutline } from '@mui/icons-material';
import { Box, Button, Divider, IconButton } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface IProps {
  title: string;
  name: string;
  sx?: SxProps<Theme>;
  className?: string;
  required?: boolean;
  background?: null | string;
  helperText?: string | ReactNode;
}

const defaultHelperText = (
  <span className={classNames('text-xs', `text-[${GREY}]`)}>
    Preferred Image Dimensions: <span className='font-medium'>5000 x 500 pixels</span>
  </span>
);

const ALLOWED_IMAGE_EXTNAMES = ['png', 'svg', 'jpg', 'jpeg', 'webp'];

const ImageUpload = ({ title, name, sx, className, required, background, helperText = defaultHelperText }: IProps) => {
  const { control, watch, setValue, formState, clearErrors, getValues } = useFormContext();
  const [picture, setPicture] = useState(getValues(name) ?? '');

  const imageStyle = useMemo(() => (background ? { background } : {}), [background]);

  useEffect(() => {
    const subscription = watch((value, { name: fieldName }) => {
      if (name === fieldName && !!value[name]) {
        setPicture(URL.createObjectURL(value[name]));
      }
    });
    return () => {
      subscription.unsubscribe();
      URL.revokeObjectURL(picture);
    };
  }, [name, picture, watch]);

  const onDelete = useCallback(() => {
    setValue(name, '');
    setPicture('');
    clearErrors('name');
  }, [setValue, name, clearErrors]);

  const error = formState?.errors?.[name];

  return (
    <Box sx={{ flex: 1, ...sx }} className={className}>
      <div className='flex items-center justify-between mb-2'>
        <span className='font-medium text-sm'>{title}</span>
        <div className='flex h-[20px] gap-1'>
          <Button
            sx={{ fontSize: '14px', fontWeight: 500 }}
            component='label'
            role={undefined}
            variant='text'
            startIcon={<UploadIcon />}
          >
            Upload New
            <Controller
              name={name}
              control={control}
              rules={{
                required: {
                  value: !!required,
                  message: `${title} is required`,
                },
                validate: (value: File | string) => {
                  if (typeof value === 'string') return true;
                  return value.type.startsWith('image') || 'File must be an image';
                },
              }}
              render={({ field: { value, onChange, ...field } }) => (
                <input
                  {...field}
                  value={value?.fileName}
                  onChange={e => {
                    onChange(e.target.files?.[0]);
                    clearErrors(name);
                  }}
                  accept={ALLOWED_IMAGE_EXTNAMES.map(ext => `.${ext}`).join(',')}
                  type='file'
                  className='hidden'
                />
              )}
            />
          </Button>
          <Divider orientation='vertical' flexItem />
          <IconButton sx={{ width: '20px' }} aria-label='delete' onClick={onDelete}>
            <DeleteOutline fontSize='small' color='error' />
          </IconButton>
        </div>
      </div>
      {picture ? (
        <img className='h-[150px] w-full object-contain bg-[#ededed] rounded-lg' style={imageStyle} src={picture} />
      ) : (
        <div className='h-[150px] bg-[#ededed] rounded-lg' style={imageStyle}></div>
      )}
      {helperText}
      {error ? <FormErrorHelperText>{error.message}</FormErrorHelperText> : null}
    </Box>
  );
};

export default ImageUpload;
